import {
  Icon,
  OspModal,
  OspToast,
  deviceDetector,
  ospThemes,
} from "@ospweb/sdk-ui";
import {
  getFileDownloadCapability,
  isLikelyFileDownloadNotSupportedError,
} from "@/src/lib/bridge/downloadCapability";
import { useAppFeatures, useBridge } from "@/src/lib/bridge/useBridge";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Chat/InlineAttachment";
import { chatClientApiBaseUrl } from "@config/config";
import { cn } from "utils";
import { rootStore } from "@store/rootStore";
import { senderSmallSecondaryButtonClass } from "../Sender/buttonStyles";
import {
  getAttachmentDisplayName,
  getSenderAttachmentChipVisual,
} from "../Sender/senderAttachmentChipStyle";
import { useFileDownloadBridge } from "@/src/lib/bridge/useFileDownloadBridge";
import { useRequestHeader } from "@/src/hooks/useRequestHeader";
import { useSnapshot } from "valtio";
import NextImage from "../NextImage";

const I18n = I18nUtil.createScoped(Translations);

const IMAGE_EXT_RE = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;

interface InlineAttachmentProps {
  "data-filename"?: string;
  "data-url"?: string;
  "data-userid"?: string;
  "data-chatid"?: string;
  "data-shareid"?: string;
  node?: unknown;
}

export const ImageLightbox = ({
  src,
  alt,
  onClose,
  onDownload,
  downloading,
}: {
  src: string;
  alt: string;
  onClose: () => void;
  onDownload: () => void;
  downloading: boolean;
}) => {
  return (
    <OspModal
      open
      onClose={onClose}
      onCancel={onClose}
      centered
      destroyOnClose
      maskClosable
      showClose={false}
      containerClassName="p-0"
      containerStyle={{
        width: "auto",
        maxHeight: "none",
        overflow: "visible",
      }}
      contentStyle={{
        padding: 0,
        margin: 0,
        width: "fit-content",
        background: "transparent",
        boxShadow: "none",
      }}
      styles={{
        wrapper: {
          zIndex: 9999,
        },
        mask: {
          backgroundColor: "rgba(0, 0, 0, 0.72)",
          backdropFilter: "blur(2px)",
        },
        content: {
          boxShadow: "none",
        },
      }}
    >
      <div
        data-openreplay-label="chatbox_inlineattachment_dialog"
        className="relative max-w-[90vw] max-h-[90vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={src}
          alt={alt}
          className="max-w-full max-h-[80vh] object-contain rounded-2"
        />
        <div className="flex items-center gap-3 mt-3">
          <button
            type="button"
            data-openreplay-label="chatbox_inlineattachment_download"
            disabled={downloading}
            onClick={onDownload}
            className={cn("button sm_button primary_button")}
          >
            <Icon name="upload_dapp" size={16} />
            <span className="ml-1 title_s">
              {downloading ? I18n.t("Loading...") : I18n.t("Download")}
            </span>
          </button>
          <button
            type="button"
            data-openreplay-label="chatbox_inlineattachment_close"
            onClick={onClose}
            className={cn(
              "button sm_button primary_button",
              senderSmallSecondaryButtonClass,
            )}
          >
            <Icon name="close" size={16} />
          </button>
        </div>
      </div>
    </OspModal>
  );
};

const InlineAttachment = (props: InlineAttachmentProps) => {
  const fileName = props["data-filename"] || "";
  const userId = props["data-userid"];
  const chatId = props["data-chatid"];
  const shareId = props["data-shareid"];

  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const previewUrlRef = useRef<string>("");
  const { headers } = useRequestHeader();
  const { fingerprint } = useSnapshot(rootStore.user);
  const appFeatures = useAppFeatures();
  const { isInApp } = useBridge();
  const { startDownload, getTask } = useFileDownloadBridge();
  const activeTask = getTask(activeTaskId);
  const fileDownloadCapability = getFileDownloadCapability(
    deviceDetector.isApp() || isInApp,
    appFeatures,
  );

  const chatApiBaseUrl = useMemo(() => {
    const env = process.env
      .NEXT_PUBLIC_ENV as keyof typeof chatClientApiBaseUrl;
    return chatClientApiBaseUrl[env] || chatClientApiBaseUrl.prod;
  }, []);

  const isImage = useMemo(() => IMAGE_EXT_RE.test(fileName), [fileName]);

  const requestHeaders = useMemo(
    () => ({
      ...headers,
      ...(fingerprint ? { guestId: String(fingerprint) } : {}),
    }),
    [headers, fingerprint],
  );

  const getAttachmentUrl = useCallback(
    (name: string) => {
      if (shareId) {
        return `${chatApiBaseUrl}/share/${encodeURIComponent(shareId)}/attachments/${encodeURIComponent(name)}`;
      }
      if (!userId || !chatId) {
        return "";
      }
      return `${chatApiBaseUrl}/attachments/${encodeURIComponent(userId)}/${encodeURIComponent(chatId)}/${encodeURIComponent(name)}`;
    },
    [chatApiBaseUrl, userId, chatId, shareId],
  );

  useEffect(() => {
    if (!isImage || !fileName || (!shareId && (!userId || !chatId))) {
      return;
    }

    let cancelled = false;

    const loadPreview = async () => {
      const requestUrl = getAttachmentUrl(fileName);
      if (!requestUrl) {
        return;
      }

      try {
        const response = await fetch(requestUrl, { headers: requestHeaders });
        if (!response.ok || cancelled) {
          return;
        }
        const blob = await response.blob();
        if (cancelled) {
          return;
        }
        const blobUrl = URL.createObjectURL(blob);
        previewUrlRef.current = blobUrl;
        setPreviewUrl(blobUrl);
      } catch {
        // silently fail
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = "";
      }
    };
  }, [
    isImage,
    fileName,
    userId,
    chatId,
    shareId,
    requestHeaders,
    getAttachmentUrl,
  ]);

  const handleDownload = useCallback(async () => {
    if (downloading) {
      return;
    }
    if (fileDownloadCapability === "unsupported") {
      OspToast.info({
        content: I18n.t("Please update the app to access files"),
      });
      return;
    }
    setDownloading(true);
    // let shouldKeepLoadingByTask = false;

    try {
      if (!shareId && (!userId || !chatId)) {
        throw new Error("Attachment context is missing");
      }
      const requestUrl = getAttachmentUrl(fileName);
      if (!requestUrl) {
        throw new Error("Attachment request URL is invalid");
      }

      if (isInApp) {
        const { taskId } = await startDownload({
          url: requestUrl,
          fileName,
          headers: requestHeaders,
          showSavePanel: true,
        });
        setActiveTaskId(taskId);
        // shouldKeepLoadingByTask = true;
        OspToast.info({ content: I18n.t("Download started") });
        return;
      }

      const response = await fetch(requestUrl, { headers: requestHeaders });
      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      if (isLikelyFileDownloadNotSupportedError(error)) {
        OspToast.info({
          content: I18n.t("Please update the app to access files"),
        });
      } else {
        OspToast.error({ content: I18n.t("Failed to load attachment") });
      }
    } finally {
      setDownloading(false);
    }
  }, [
    chatId,
    downloading,
    fileName,
    getAttachmentUrl,
    fileDownloadCapability,
    requestHeaders,
    shareId,
    isInApp,
    startDownload,
    userId,
  ]);

  useEffect(() => {
    if (!activeTask) {
      return;
    }
    if (activeTask.status === "completed") {
      OspToast.success({ content: I18n.t("Download completed") });
      setDownloading(false);
      setActiveTaskId(null);
      return;
    }
    if (activeTask.status === "failed") {
      OspToast.error({
        content: activeTask.errorMessage || I18n.t("Failed to load attachment"),
      });
      setDownloading(false);
      setActiveTaskId(null);
    }
  }, [activeTask]);

  if (!fileName) {
    return null;
  }
  const ext = fileName.split(".").pop()?.toUpperCase() || I18n.t("unknown");
  const chipVisual = getSenderAttachmentChipVisual(fileName);
  const chipDisplayName = getAttachmentDisplayName(fileName);

  return (
    <>
      <button
        type="button"
        data-openreplay-label="chatbox_inlineattachment_open"
        className={cn("button flex group rounded-3", {
          "max-w-140 max-h-140 bg-background_solid_neutral_primary loading:bg-background_solid_neutral_tertiary loading:skeleton loading:size-60 md:loading:size-140":
            isImage,
          "p-1.5 bg-background_white_overlay5 gap-1 items-center": !isImage,
        })}
        onClick={() => {
          if (previewUrl && isImage) {
            return setLightboxOpen(true);
          }
          return handleDownload();
        }}
        data-loading={(isImage && !previewUrl) || downloading}
      >
        {!isImage && (
          <>
            <div className="flex min-w-0 items-center gap-2">
              <div
                className={cn(
                  "relative flex size-8 shrink-0 items-center justify-center rounded-1.5",
                  chipVisual.iconBoxClassName,
                )}
              >
                <Icon
                  name={chipVisual.iconName}
                  size={20}
                  color={ospThemes.colors.content_inverse_primary}
                />
              </div>
              <div className="flex max-w-60 min-w-0 flex-col gap-1">
                <span className="body_s block w-full truncate text-left text-content_secondary">
                  {chipDisplayName}
                </span>
                <span className="body_xs block w-full truncate text-left text-content_tertiary">
                  {ext}
                </span>
              </div>
            </div>
            <Icon
              name={downloading ? "ic_loading2" : "upload_dapp"}
              size={24}
              color={ospThemes.colors.content_tertiary}
              className={cn({ "animate-spin": downloading })}
            />
          </>
        )}
        <NextImage
          src={previewUrl}
          alt={fileName}
          className={cn(
            "rounded-2 object-cover align-middle max-w-full md:max-w-140 max-h-140 object-cover size-auto group-loading:hidden",
            { hidden: !isImage },
          )}
        />
      </button>
      {lightboxOpen && previewUrl && (
        <ImageLightbox
          src={previewUrl}
          alt={fileName}
          onClose={() => setLightboxOpen(false)}
          onDownload={handleDownload}
          downloading={downloading}
        />
      )}
    </>
  );
};

export default InlineAttachment;
